// ── Data types shared across all builder nodes ────────────────────────────────

// Node kinds for visual workflow builder
// v1: trigger, classify, branch, action, delay, end
// v2: adds switch, merge, stop for more visual control flow
export type NodeKind = 'trigger' | 'classify' | 'branch' | 'switch' | 'merge' | 'action' | 'delay' | 'end' | 'stop' | 'loop';

// ── Branch node handle IDs for if/else paths ──────────────────────────────────
export const BRANCH_HANDLE_TRUE = 'true';
export const BRANCH_HANDLE_FALSE = 'false';

// ── Switch node handle IDs ────────────────────────────────────────────────────
export const SWITCH_HANDLE_DEFAULT = 'default';
export const getSwitchCaseHandle = (index: number) => `case_${index}`;

// ── Loop node handle IDs ──────────────────────────────────────────────────────
// 'body' runs once per item (connect to a single action to run per iteration);
// 'after' continues the main flow once all iterations finish.
export const LOOP_HANDLE_BODY = 'body';
export const LOOP_HANDLE_AFTER = 'after';

// Loop iteration execution modes (mirror the backend LoopExecutionMode enum).
export type LoopExecutionMode = 'serial' | 'parallel' | 'parallel_limited';

// React Flow requires node data to satisfy Record<string, unknown>
// We use intersection types to keep specific property types while satisfying the constraint.

export interface TriggerNodeData extends Record<string, unknown> {
  kind: 'trigger';
  label: string;
  eventType: string;       // e.g. TICKET_CREATED
  channel: string;         // 'any' | 'email' | 'chat' | 'call' | 'whatsapp'
  ticketTypeId?: string;   // optional — filter trigger to a specific ticket type
  ticketTypeName?: string;
}

export interface ClassifyNodeData extends Record<string, unknown> {
  kind: 'classify';
  label: string;
}

export interface BranchNodeData extends Record<string, unknown> {
  kind: 'branch';
  label: string;
  field: string;           // entity field to branch on, e.g. 'entity.ticketTypeId'
  operator: string;        // e.g. 'equals'
  value: string;           // comparison value
  displayLabel?: string;   // human-readable condition, e.g. 'Ticket type is Refund'
}

export interface ActionNodeData extends Record<string, unknown> {
  kind: 'action';
  label: string;
  actionType: string;      // ActionType enum value
  config: Record<string, unknown>;
  displaySummary?: string; // e.g. 'Assign to Billing Team'
}

export interface DelayNodeData extends Record<string, unknown> {
  kind: 'delay';
  label: string;
  delaySeconds: number;
}

export interface EndNodeData extends Record<string, unknown> {
  kind: 'end';
  label: string;
}

// ── v2 Node Types ─────────────────────────────────────────────────────────────

export interface SwitchNodeData extends Record<string, unknown> {
  kind: 'switch';
  label: string;
  field: string;           // Field to switch on, e.g. 'entity.status'
  cases: Array<{
    value: string | number | boolean;
    label?: string;        // Display label for the case
  }>;
  hasDefault: boolean;     // Whether to include a default case
  displayLabel?: string;   // Human-readable summary
}

export interface MergeNodeData extends Record<string, unknown> {
  kind: 'merge';
  label: string;
}

export interface StopNodeData extends Record<string, unknown> {
  kind: 'stop';
  label: string;
  reason?: string;         // Optional reason for stopping
}

export interface LoopNodeData extends Record<string, unknown> {
  kind: 'loop';
  label: string;
  itemsPath: string;       // Array to iterate, e.g. 'entity.lineItems'
  itemVariable: string;    // Name the current item is bound to, e.g. 'lineItem'
  mode: LoopExecutionMode; // serial | parallel | parallel_limited
  concurrency?: number;    // Used when mode === 'parallel_limited'
  maxIterations?: number;  // Safety cap (backend default 1000)
  continueOnError: boolean;// Keep iterating after a failed iteration
  outputVariable: string;  // context.metadata key for the aggregate result
  displayLabel?: string;   // Human-readable summary
}

export type WorkflowNodeData =
  | TriggerNodeData
  | ClassifyNodeData
  | BranchNodeData
  | SwitchNodeData
  | MergeNodeData
  | ActionNodeData
  | DelayNodeData
  | EndNodeData
  | StopNodeData
  | LoopNodeData;

// ── Org data types loaded from APIs ──────────────────────────────────────────

export interface TicketType {
  id: string;
  name: string;
  slug: string;
  color?: string;
  icon?: string;
}

export interface Team {
  id: string;
  name: string;
}

export interface Agent {
  id: string;
  name: string;
  email: string;
}

export interface PipelineStage {
  id: string;
  name: string;
  pipelineId: string;
  pipelineName: string;
}

export interface CustomField {
  id: string;
  name: string;
  key: string;
  fieldType: string;
}

// Response mapping for webhook actions
export interface ResponseMapping {
  responseField: string;    // JSON path in response, e.g. "data" or "brokerLink"
  targetField: string;      // Custom field key to store the value
  targetFieldName?: string; // Display name for UI
}

// ── Serialized rule (output of serializer, input to API) ─────────────────────

export interface SerializedRule {
  name: string;
  description?: string;
  triggerType: string;
  triggerConditions: {
    logic: 'AND' | 'OR';
    conditions: SerializedCondition[];
  };
  actions: SerializedAction[];
  stopOnMatch: boolean;
  isActive: boolean;
}

export interface SerializedCondition {
  field: string;
  operator: string;
  value?: unknown;
}

export interface SerializedAction {
  type: string;
  config: Record<string, unknown>;
  delay?: number;
  // For conditional (if/else) branching
  conditional?: {
    condition: {
      logic: 'AND' | 'OR';
      conditions: SerializedCondition[];
    };
    thenActions: SerializedAction[];
    elseActions?: SerializedAction[];
  };
  // For switch/case branching
  switch?: {
    field: string;
    cases: Array<{
      value: string | number | boolean;
      actions: SerializedAction[];
    }>;
    defaultActions?: SerializedAction[];
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// v2 Node-Based Workflow Schema
// This is the new visual-friendly format that maps directly to visual nodes
// ══════════════════════════════════════════════════════════════════════════════

export type WorkflowSchemaVersion = 1 | 2;

// v2 Node types that match the backend WorkflowNodeType enum
export type V2NodeType =
  | 'trigger'
  | 'if_branch'
  | 'switch'
  | 'merge'
  | 'action'
  | 'delay'
  | 'wait_for_event'
  | 'loop'
  | 'end'
  | 'stop';

// v2 Node configuration types
export interface V2TriggerConfig {
  eventType: string;
  channel?: string;
  conditions?: {
    logic: 'AND' | 'OR';
    conditions: SerializedCondition[];
  };
}

export interface V2IfBranchConfig {
  condition: {
    logic: 'AND' | 'OR';
    conditions: SerializedCondition[];
  };
}

export interface V2SwitchConfig {
  field: string;
  cases: Array<{
    value: string | number | boolean;
    handle: string;  // e.g., 'case_0', 'case_1'
    /** Readable name for an id value (e.g. a stage). Display only. */
    label?: string;
  }>;
  hasDefault?: boolean;
}

export interface V2ActionConfig {
  actionType: string;
  config: Record<string, unknown>;
}

export interface V2DelayConfig {
  delaySeconds: number;
}

export interface V2StopConfig {
  reason?: string;
  logEvent?: boolean;
}

export interface V2LoopConfig {
  itemsPath: string;
  itemVariable: string;
  mode: LoopExecutionMode;
  concurrency?: number;
  maxIterations?: number;
  continueOnError: boolean;
  outputVariable: string;
}

export type V2NodeConfig =
  | V2TriggerConfig
  | V2IfBranchConfig
  | V2SwitchConfig
  | V2ActionConfig
  | V2DelayConfig
  | V2StopConfig
  | V2LoopConfig
  | Record<string, unknown>;

// v2 Workflow Node
export interface V2WorkflowNode {
  id: string;
  type: V2NodeType;
  label?: string;
  position?: { x: number; y: number };
  config: V2NodeConfig;
}

// v2 Workflow Edge
export interface V2WorkflowEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle?: string;  // 'true', 'false', 'case_0', 'default', etc.
  label?: string;
}

// v2 Workflow Definition
export interface V2WorkflowDefinition {
  version: '2.0';
  entryNodeId: string;
  nodes: V2WorkflowNode[];
  edges: V2WorkflowEdge[];
}

// v2 Serialized Rule (combines rule metadata with workflow definition)
export interface V2SerializedRule {
  name: string;
  description?: string;
  schemaVersion: 2;
  workflow: V2WorkflowDefinition;
  stopOnMatch: boolean;
  isActive: boolean;
}

// ── Trigger event type options ────────────────────────────────────────────────

export const TRIGGER_EVENT_OPTIONS = [
  { value: 'TICKET_CREATED',          label: 'Ticket is created' },
  { value: 'TICKET_UPDATED',          label: 'Ticket is updated' },
  { value: 'TICKET_STATUS_CHANGED',   label: 'Ticket status changes' },
  { value: 'TICKET_ASSIGNED',         label: 'Ticket is assigned' },
  { value: 'TICKET_PRIORITY_CHANGED', label: 'Ticket priority changes' },
  { value: 'MESSAGE_RECEIVED',        label: 'Customer replies' },
  { value: 'MESSAGE_SENT',            label: 'Agent replies' },
  { value: 'CHAT_STARTED',            label: 'Chat session starts' },
  { value: 'CHAT_ENDED',              label: 'Chat session ends' },
  { value: 'SLA_BREACH_WARNING',      label: 'SLA about to breach' },
  { value: 'SLA_BREACHED',            label: 'SLA breached' },
  { value: 'CUSTOMER_CREATED',        label: 'New customer created' },
  { value: 'WEBHOOK_RECEIVED',        label: 'Webhook received' },
  { value: 'LINE_ITEM_QUOTE_VALIDATION_FAILED', label: 'Quote generation failed (missing fields)' },
] as const;

/** What each reply trigger fires on (shown under the trigger picker). */
export const REPLY_TRIGGER_HINTS: Record<string, string> = {
  MESSAGE_RECEIVED:
    "Runs when the customer replies on a ticket (email, live chat or portal). Not for the message that opens the ticket — use \"Ticket is created\" for that.",
  MESSAGE_SENT:
    'Runs when an agent sends a reply to the customer. Internal notes, drafts and replies sent by automations do not count.',
};

export const CHANNEL_OPTIONS = [
  { value: 'any',      label: 'Any channel' },
  { value: 'email',    label: 'Email' },
  { value: 'chat',     label: 'Live chat' },
  { value: 'call',     label: 'Phone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms',      label: 'SMS' },
] as const;

export const ACTION_TYPE_OPTIONS = [
  { value: 'classify_ticket_type',  label: 'Auto-classify ticket type',  icon: '🤖' },
  { value: 'assign_team',           label: 'Assign to team',             icon: '👥' },
  { value: 'assign_agent',          label: 'Assign to agent',            icon: '👤' },
  { value: 'auto_assign',           label: 'Auto-assign agent (round robin, least busy…)', icon: '🎯' },
  { value: 'set_priority',          label: 'Set priority',               icon: '🔺' },
  { value: 'set_status',            label: 'Set status',                 icon: '🔄' },
  { value: 'move_to_pipeline_stage',label: 'Move to pipeline stage',     icon: '📋' },
  { value: 'send_email',            label: 'Send email to customer',     icon: '📧' },
  { value: 'send_internal_note',    label: 'Add internal note',          icon: '📝' },
  { value: 'send_notification',     label: 'Send notification',          icon: '🔔' },
  { value: 'trigger_webhook',       label: 'Call external API',          icon: '🔗' },
  { value: 'set_custom_field',      label: 'Set custom field',           icon: '⚙️' },
  { value: 'add_tag',               label: 'Add tag',                    icon: '🏷️' },
  { value: 'create_ticket',         label: 'Create linked ticket',       icon: '🎫' },
  { value: 'fetch_line_items',      label: 'Fetch line items',           icon: '📋' },
  { value: 'parse_email_and_update_line_item', label: 'Parse email & update line item (AI)', icon: '🧠' },
  { value: 'update_line_item',      label: 'Update line item',           icon: '📦' },
  { value: 'increment_retry_counter', label: 'Increment retry counter',  icon: '🔄' },
  // Branching/Control flow actions
  { value: 'conditional',           label: 'If/Else Condition',          icon: '🔀' },
  { value: 'switch',                label: 'Switch/Case',                icon: '🔃' },
  { value: 'stop_workflow',         label: 'Stop Workflow',              icon: '🛑' },
] as const;

/**
 * Techniques for the "Auto-assign agent" action. Same values and wording as a
 * pipeline's Auto-Assignment Strategy (Settings → Pipelines → Routing).
 */
export const AUTO_ASSIGN_STRATEGY_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin', description: 'Distribute evenly across agents, taking turns' },
  { value: 'least_busy',  label: 'Least Busy',  description: 'Assign to the agent with the fewest open tickets' },
  { value: 'random',      label: 'Random',      description: 'Randomly assign to one of the agents' },
] as const;

/** One-line node summary for an "Auto-assign agent" action. */
export function autoAssignSummary(
  config: Record<string, unknown>,
  teams: Array<{ id: string; name: string }>,
): string {
  const technique = AUTO_ASSIGN_STRATEGY_OPTIONS.find((o) => o.value === config.strategy)?.label;
  if (!technique) return 'Choose a technique';
  const teamId = config.teamId as string | undefined;
  const pool = teamId ? teams.find((t) => t.id === teamId)?.name ?? 'selected team' : "ticket's team";
  return `${technique} · ${pool}`;
}

export const BRANCH_FIELD_OPTIONS = [
  { value: 'entity.ticketTypeId',    label: 'Ticket type',       valueType: 'ticketType' },
  { value: 'entity.stageId',         label: 'Pipeline stage',    valueType: 'stage' },
  { value: 'entity.priority',        label: 'Priority',          valueType: 'priority' },
  { value: 'entity.status',          label: 'Status',            valueType: 'status' },
  { value: 'entity.channel',         label: 'Channel',           valueType: 'channel' },
  { value: 'entity.lineItemDetails', label: 'Line item details', valueType: 'existence' },
] as const;

export const EXISTENCE_OPTIONS = [
  { value: 'is_not_empty', label: 'exists (is not empty)' },
  { value: 'is_empty',     label: 'does not exist (is empty)' },
] as const;

export const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high',   label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low',    label: 'Low' },
] as const;

export const STATUS_OPTIONS = [
  { value: 'new',         label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending',     label: 'Pending' },
  { value: 'resolved',    label: 'Resolved' },
  { value: 'closed',      label: 'Closed' },
] as const;
